import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, AlertTriangle, Check } from 'lucide-react';

interface NetPiece {
  id: string;
  x: number;
  y: number;
  connections: string[];
  removed: boolean;
  canRemove: boolean;
}

interface NetRemovalGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

function generatePuzzle(): NetPiece[] {
  // Create a puzzle where pieces must be removed in a specific order
  // Pieces can only be removed if they have 1 or fewer unremoved connections
  const pieces: NetPiece[] = [
    // Outer ring (can be removed first)
    { id: 'a1', x: 20, y: 20, connections: ['b1'], removed: false, canRemove: true },
    { id: 'a2', x: 80, y: 20, connections: ['b2'], removed: false, canRemove: true },
    { id: 'a3', x: 20, y: 80, connections: ['b3'], removed: false, canRemove: true },
    { id: 'a4', x: 80, y: 80, connections: ['b4'], removed: false, canRemove: true },

    // Middle ring
    { id: 'b1', x: 35, y: 35, connections: ['a1', 'c1', 'b2'], removed: false, canRemove: false },
    { id: 'b2', x: 65, y: 35, connections: ['a2', 'c1', 'b1'], removed: false, canRemove: false },
    { id: 'b3', x: 35, y: 65, connections: ['a3', 'c1', 'b4'], removed: false, canRemove: false },
    { id: 'b4', x: 65, y: 65, connections: ['a4', 'c1', 'b3'], removed: false, canRemove: false },

    // Center (otter is here!)
    { id: 'c1', x: 50, y: 50, connections: ['b1', 'b2', 'b3', 'b4'], removed: false, canRemove: false },
  ];

  return pieces;
}

function updateCanRemove(pieces: NetPiece[]): NetPiece[] {
  return pieces.map(piece => {
    if (piece.removed) return piece;

    // Count unremoved connections
    const activeConnections = piece.connections.filter(connId => {
      const connected = pieces.find(p => p.id === connId);
      return connected && !connected.removed;
    });

    // Can remove if 1 or fewer active connections (not counting self)
    return {
      ...piece,
      canRemove: activeConnections.length <= 1,
    };
  });
}

export function NetRemovalGame({ onComplete, onBack, speciesId, otterName }: NetRemovalGameProps) {
  const [pieces, setPieces] = useState<NetPiece[]>(() => updateCanRemove(generatePuzzle()));
  const [moves, setMoves] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showError, setShowError] = useState(false);

  const totalPieces = pieces.length;
  const removedCount = pieces.filter(p => p.removed).length;
  const bestMoves = totalPieces; // Theoretically optimal

  const handlePieceClick = useCallback((piece: NetPiece) => {
    if (isComplete || piece.removed) return;

    if (piece.canRemove) {
      // Remove the piece
      setPieces(prev => {
        const updated = prev.map(p =>
          p.id === piece.id ? { ...p, removed: true } : p
        );
        return updateCanRemove(updated);
      });
      setMoves(m => m + 1);

      // Check for completion
      if (removedCount + 1 >= totalPieces - 1) {
        // Last piece (center) is freed!
        setIsComplete(true);
        const stars = mistakes === 0 ? 3 : mistakes <= 2 ? 2 : 1;
        setTimeout(() => onComplete(Math.max(0, 100 - mistakes * 10), stars), 1500);
      }
    } else {
      // Wrong piece - show error
      setMistakes(m => m + 1);
      setShowError(true);
      setTimeout(() => setShowError(false), 500);
    }
  }, [isComplete, removedCount, totalPieces, mistakes, onComplete]);

  const resetPuzzle = useCallback(() => {
    setPieces(updateCanRemove(generatePuzzle()));
    setMoves(0);
    setMistakes(0);
    setIsComplete(false);
  }, []);

  return (
    <div className="fixed inset-0 bg-otter-900 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Net Removal</h2>
          <button onClick={resetPuzzle} className="text-white/60 text-sm">
            Reset
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-around text-sm">
          <div className="text-center">
            <div className="text-white/60">Moves</div>
            <div className="text-white font-bold">{moves}</div>
          </div>
          <div className="text-center">
            <div className="text-white/60">Mistakes</div>
            <div className={`font-bold ${mistakes > 0 ? 'text-red-400' : 'text-white'}`}>
              {mistakes}
            </div>
          </div>
          <div className="text-center">
            <div className="text-white/60">Progress</div>
            <div className="text-white font-bold">
              {removedCount}/{totalPieces - 1}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="px-4 py-2 bg-yellow-900/30">
        <p className="text-yellow-200 text-sm text-center flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Remove net pieces carefully! Start from the edges.
        </p>
      </div>

      {/* Game area */}
      <div className="flex-1 relative overflow-hidden p-4">
        <div className="w-full h-full relative bg-blue-900/30 rounded-2xl border-2 border-blue-700/50">
          {/* Net lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {pieces.map(piece =>
              piece.connections.map(connId => {
                const connected = pieces.find(p => p.id === connId);
                if (!connected || piece.removed || connected.removed) return null;
                if (piece.id > connId) return null; // Avoid duplicate lines

                return (
                  <motion.line
                    key={`${piece.id}-${connId}`}
                    x1={`${piece.x}%`}
                    y1={`${piece.y}%`}
                    x2={`${connected.x}%`}
                    y2={`${connected.y}%`}
                    stroke="#22c55e"
                    strokeWidth="3"
                    strokeOpacity="0.6"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  />
                );
              })
            )}
          </svg>

          {/* Net pieces */}
          {pieces.map(piece => {
            const isCenter = piece.id === 'c1';

            return (
              <motion.button
                key={piece.id}
                onClick={() => !isCenter && handlePieceClick(piece)}
                disabled={piece.removed || isCenter}
                className={`
                  absolute w-10 h-10 -ml-5 -mt-5 rounded-full
                  flex items-center justify-center
                  transition-all duration-200
                  ${piece.removed
                    ? 'opacity-0 scale-0'
                    : isCenter
                      ? 'bg-amber-600 text-2xl cursor-default'
                      : piece.canRemove
                        ? 'bg-green-500 hover:bg-green-400 cursor-pointer shadow-lg shadow-green-500/50'
                        : 'bg-red-700 hover:bg-red-600 cursor-pointer'
                  }
                `}
                style={{
                  left: `${piece.x}%`,
                  top: `${piece.y}%`,
                }}
                animate={{
                  scale: piece.removed ? 0 : 1,
                  opacity: piece.removed ? 0 : 1,
                }}
                whileTap={!isCenter && !piece.removed ? { scale: 0.9 } : {}}
              >
                {isCenter ? (
                  '🦦'
                ) : piece.canRemove ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-white text-xs font-bold">X</span>
                )}
              </motion.button>
            );
          })}

          {/* Error shake effect */}
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: [0, -10, 10, -10, 10, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 bg-red-500/20 rounded-2xl pointer-events-none"
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 bg-black/30">
        <div className="flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500" />
            <span className="text-white/70">Safe to remove</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-700" />
            <span className="text-white/70">Not yet</span>
          </div>
        </div>
      </div>

      {/* Completion overlay */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/70 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="bg-green-700 rounded-2xl p-6 text-center"
            >
              <div className="text-6xl mb-3">🦦</div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {otterName} is Free!
              </h3>
              <p className="text-white/80 mb-2">
                Completed in {moves} moves
                {mistakes > 0 && ` with ${mistakes} mistake${mistakes > 1 ? 's' : ''}`}
              </p>
              <div className="flex justify-center gap-1 mt-3">
                {[1, 2, 3].map(i => (
                  <Star
                    key={i}
                    className={`w-8 h-8 ${
                      (mistakes === 0 && i <= 3) || (mistakes <= 2 && i <= 2) || i === 1
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
